'use client';

import cx from 'classnames';
import Image from 'next/image';

import { useTrackEventAmplitude } from '../../../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../../../design-system/components/form/button/Button';
import { BrandHandMarkArrowVariant1 } from '../../../../../design-system/components/illustration/BrandHandMarkArrowVariant1';
import { RATIO, Ratio } from '../../../../../design-system/components/ratio/Ratio';
import { Typography } from '../../../../../design-system/components/typography/Typography';
import { Carousel, useCarousel } from '../../../../../domains/Layout/Carousel/Carousel';
import {
    CarouselBlockSliceType,
    CarouselItemType,
} from '../../../../../domains/Prismic/BrandPages/BrandPagesDocument';
import { EvaneosPrismicRichText } from '../../../../../domains/Prismic/EvaneosPrismicRichText';

import style from './CarouselBlockSlice.module.css';

export type CarouselBlockSliceProps = CarouselBlockSliceType;

export function CarouselBlockSlice({
    items,
    title,
    description,
    sliceIndex,
}: CarouselBlockSliceProps & { sliceIndex?: number }) {
    const carouselProps = useCarousel();

    return (
        <div className={cx('row', style.wrapper, { [style.withTitle]: title })}>
            {title || description ? (
                <div className={cx('col-12', { 'col-lg-4': title }, style.content)}>
                    {title ? (
                        <div className={style.title}>
                            <BrandHandMarkArrowVariant1 className={style.handMark} />
                            <Typography as="h2" scale="secondary-xl-bold">
                                {title}
                            </Typography>
                        </div>
                    ) : null}
                    {description ? (
                        <Typography
                            as="div"
                            scale={title ? 'primary-body-regular' : 'primary-m-extra-bold'}
                            className={cx({ [style.fullWidthDescription]: !title })}
                        >
                            {Array.isArray(description) ? (
                                <EvaneosPrismicRichText field={description} />
                            ) : null}
                        </Typography>
                    ) : null}
                </div>
            ) : null}
            <div className={cx('col-12', { 'col-lg-8': title }, style.carouselWrapper)}>
                <Carousel
                    {...carouselProps}
                    slidesSize="large"
                    slidesVisibleRules={{
                        sm: 2,
                        md: 2,
                        lg: 2,
                        xl: title ? 2 : 3,
                        xxl: title ? 2 : 3,
                    }}
                    trackingInformation={{
                        navClick: {
                            block: 'carousel',
                            block_index: sliceIndex ? sliceIndex + 1 : 0,
                            block_title: title ?? undefined,
                        },
                    }}
                >
                    {items.map((item, index) => (
                        <CarouselCard
                            key={`carousel-item-${index}`}
                            picture={item.picture}
                            title={item.title}
                            description={item.description}
                            ctaLabel={item.ctaLabel}
                            ctaLink={item.ctaLink}
                            sliceIndex={sliceIndex}
                            cardIndex={index}
                            sliceTitle={title}
                            target={item.target}
                        />
                    ))}
                </Carousel>
            </div>
        </div>
    );
}

type CarouselCardProps = CarouselItemType & {
    sliceIndex?: number;
    cardIndex: number;
    sliceTitle: CarouselBlockSliceProps['title'];
};

function CarouselCard({
    picture,
    title,
    description,
    ctaLabel,
    ctaLink,
    sliceIndex,
    cardIndex,
    sliceTitle,
    target,
}: CarouselCardProps) {
    const { trackEvent } = useTrackEventAmplitude();

    return (
        <div className={style.card}>
            {picture?.url ? (
                <div className={style.imageWrapper}>
                    <Ratio ratio={CarouselCard.PICTURE_RATIO}>
                        <Image
                            className={style.image}
                            alt={picture?.alt}
                            src={picture?.url}
                            unoptimized
                            fill
                            loading="lazy"
                        />
                    </Ratio>
                </div>
            ) : null}
            <Typography as="h3" scale="primary-m-extra-bold">
                {title}
            </Typography>
            <Typography as="div" scale="primary-body-regular">
                {description}
            </Typography>

            {ctaLabel && ctaLink?.url ? (
                <div className={style.ctaWrapper}>
                    <Button
                        priority="secondary-filled"
                        href={ctaLink?.url}
                        target={target || undefined}
                        onClick={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.carouselDetailsClicked({
                                    ...defaultProperties,
                                    block_index: sliceIndex ? sliceIndex + 1 : 0,
                                    block_title: sliceTitle ?? undefined,
                                    card_title: title ?? '',
                                    card_number: cardIndex + 1,
                                    url_redirection: ctaLink?.url ?? '',
                                });
                            });
                        }}
                    >
                        {ctaLabel}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}

CarouselCard.PICTURE_RATIO = RATIO['4/3'];
